// ts-rest contract for the Client Reviews hub (#211 / ADR-0014).
//
// The Client Review is the dated periodic-review *occasion* for a whole client
// relationship, distinct from the Fact Find *artefact* it refreshes. This
// contract is a per-view presentation projection of the ClientReview /
// ClientReviewDigest ontology — authored for the register + insight-forward
// cockpit, not a domain model.
//
// Endpoints under /api/client-review/*:
//   GET  /api/client-review/register                  — one row per client
//   GET  /api/client-review/:clientId                  — client header + open review
//   GET  /api/client-review/:clientId/digest           — "Since last review" change-set
//   POST /api/client-review/:clientId/open             — open a review (ad-hoc)
//   POST /api/client-review/review/:reviewId/start     — due_soon → in_progress
//   POST /api/client-review/review/:reviewId/step      — disposition one step
//   POST /api/client-review/review/:reviewId/complete  — sign off + complete
//   POST /api/client-review/review/:reviewId/cancel    — cancel

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const clientReviewStatusSchema = z.enum([
    'due_soon',
    'in_progress',
    'completed',
    'cancelled',
]);
export type ClientReviewStatus = z.infer<typeof clientReviewStatusSchema>;

export const clientReviewOriginSchema = z.enum(['calendar', 'life_event', 'ad_hoc']);
export type ClientReviewOrigin = z.infer<typeof clientReviewOriginSchema>;

export const reviewStepKeySchema = z.enum(['fact_find', 'suitability', 'cfl', 'review_letter']);
export type ReviewStepKey = z.infer<typeof reviewStepKeySchema>;

export const stepDispositionSchema = z.enum(['confirmed_unchanged', 'updated']);
export type StepDisposition = z.infer<typeof stepDispositionSchema>;

/** Coarse per-anchor freshness band shown in the register. */
export const anchorFreshnessSchema = z.enum(['fresh', 'due_soon', 'overdue', 'never']);
export type AnchorFreshness = z.infer<typeof anchorFreshnessSchema>;

/** The register filter the IM triages by. */
export const registerFilterSchema = z.enum(['all', 'due_soon', 'in_flight', 'overdue']);
export type RegisterFilter = z.infer<typeof registerFilterSchema>;

// ---------------------------------------------------------------------------
// Register row — one per client
// ---------------------------------------------------------------------------

export const reviewStepStateSchema = z.object({
    step: reviewStepKeySchema,
    disposition: stepDispositionSchema.nullable(),
});
export type ReviewStepState = z.infer<typeof reviewStepStateSchema>;

export const clientReviewSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    status: clientReviewStatusSchema,
    originTrigger: clientReviewOriginSchema,
    anchorAt: z.string().nullable(),
    dueAt: z.string(),
    openedAt: z.string(),
    steps: z.array(reviewStepStateSchema),
    signedOffBy: z.string().nullable(),
    signedOffAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    outcomeNote: z.string().nullable(),
    escalationLevel: z.number().int(),
    escalationReason: z.string().nullable(),
});
export type ClientReview = z.infer<typeof clientReviewSchema>;

export const registerRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    segment: z.string(),
    serviceType: z.string(),
    /** Per-anchor freshness across the three regulatory anchors. */
    factFindFreshness: anchorFreshnessSchema,
    suitabilityFreshness: anchorFreshnessSchema,
    cflFreshness: anchorFreshnessSchema,
    /** The next review-due date (the suitability anchor + cadence), or null. */
    nextReviewDueAt: z.string().nullable(),
    /** The open review's status, if any. */
    openReviewStatus: clientReviewStatusSchema.nullable(),
    openReviewId: z.string().nullable(),
    /** True when any open review is escalated/overdue. */
    overdue: z.boolean(),
});
export type RegisterRow = z.infer<typeof registerRowSchema>;

export const registerResponseSchema = z.object({
    rows: z.array(registerRowSchema),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// ---------------------------------------------------------------------------
// Client header + open review (the cockpit header)
// ---------------------------------------------------------------------------

export const clientHeaderSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    segment: z.string(),
    serviceType: z.string(),
    riskRating: z.string(),
    /** Mandate reference for the active mandate, or null when none is held. */
    mandateRef: z.string().nullable(),
    /** Total portfolio value (GBP) from the latest allocation snapshot, or null. */
    aumGbp: z.number().nullable(),
    factFindFreshness: anchorFreshnessSchema,
    suitabilityFreshness: anchorFreshnessSchema,
    cflFreshness: anchorFreshnessSchema,
    factFindExpiresAt: z.string().nullable(),
    lastSuitabilityReviewAt: z.string().nullable(),
    cflAssessedAt: z.string().nullable(),
    nextReviewDueAt: z.string().nullable(),
});
export type ClientHeader = z.infer<typeof clientHeaderSchema>;

export const clientReviewDetailSchema = z.object({
    header: clientHeaderSchema,
    /** The currently open (due_soon|in_progress) review, if any. */
    openReview: clientReviewSchema.nullable(),
    /** All reviews for the client, newest first. */
    reviews: z.array(clientReviewSchema),
});
export type ClientReviewDetail = z.infer<typeof clientReviewDetailSchema>;

// ---------------------------------------------------------------------------
// Digest — the "Since last review" change-set
// ---------------------------------------------------------------------------

export const digestLifeEventSchema = z.object({
    id: z.string(),
    category: z.string(),
    summary: z.string(),
    sourceQuote: z.string(),
    callId: z.string(),
    at: z.string(),
});

export const digestPrefillSchema = z.object({
    id: z.string(),
    sectionId: z.string(),
    questionId: z.string(),
    sourceCallId: z.string().nullable(),
    createdAt: z.string().nullable(),
});

export const digestVulnerabilitySchema = z.object({
    id: z.string(),
    driver: z.string(),
    subCategory: z.string(),
    severity: z.string(),
    status: z.string(),
    raisedAt: z.string(),
});

export const digestSuitabilityAlertSchema = z.object({
    id: z.string(),
    alertType: z.string(),
    severity: z.string(),
    description: z.string(),
    raisedAt: z.string(),
});

export const driftLineSchema = z.object({
    assetClass: z.string(),
    currentPct: z.number(),
    targetPct: z.number(),
    tolerancePct: z.number(),
    deltaPct: z.number(),
    breached: z.boolean(),
    direction: z.enum(['over', 'under', 'within']),
});

export const portfolioDriftSchema = z.object({
    hasData: z.boolean(),
    lines: z.array(driftLineSchema),
    breachCount: z.number().int(),
});

export const clientReviewDigestSchema = z.object({
    clientId: z.string(),
    lastReviewAt: z.string().nullable(),
    lifeEvents: z.array(digestLifeEventSchema),
    pendingPrefills: z.array(digestPrefillSchema),
    vulnerabilityChanges: z.array(digestVulnerabilitySchema),
    suitabilityAlerts: z.array(digestSuitabilityAlertSchema),
    portfolioDrift: portfolioDriftSchema,
    contactCount: z.number().int(),
});
export type ClientReviewDigest = z.infer<typeof clientReviewDigestSchema>;

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export const openReviewRequestSchema = z.object({
    /** ad_hoc from the Desk "Start review now" control; the cadence sweep and
     * PULSE open calendar / life_event reviews server-side. */
    originTrigger: clientReviewOriginSchema.default('ad_hoc'),
    /** Optional explicit due date; defaults to today when ad-hoc. */
    dueAt: z.string().optional(),
});
export type OpenReviewRequest = z.infer<typeof openReviewRequestSchema>;

export const dispositionStepRequestSchema = z.object({
    step: reviewStepKeySchema,
    disposition: stepDispositionSchema,
});
export type DispositionStepRequest = z.infer<typeof dispositionStepRequestSchema>;

// Sign-off identity is the authenticated actor (derived server-side from
// ctx.actor), not a client-supplied value — the body is empty.
export const completeReviewRequestSchema = z.object({});
export type CompleteReviewRequest = z.infer<typeof completeReviewRequestSchema>;

export const cancelReviewRequestSchema = z.object({
    cancellationReason: z.string().min(1),
});
export type CancelReviewRequest = z.infer<typeof cancelReviewRequestSchema>;

const okBody = z.object({ ok: z.boolean(), review: clientReviewSchema });
import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const clientReviewContract = c.router(
    {
        listRegister: {
            method: 'GET',
            path: '/client-review/register',
            query: z.object({ filter: registerFilterSchema.optional() }).optional(),
            responses: { 200: registerResponseSchema },
            summary: 'Client Reviews register — one row per client with anchor freshness',
        },
        getDetail: {
            method: 'GET',
            path: '/client-review/:clientId',
            responses: { 200: clientReviewDetailSchema, 404: errorBody },
            summary: 'Client header + the open review + review history',
        },
        getDigest: {
            method: 'GET',
            path: '/client-review/:clientId/digest',
            responses: { 200: clientReviewDigestSchema, 404: errorBody },
            summary: 'The "Since last review" change-set for a client',
        },
        openReview: {
            method: 'POST',
            path: '/client-review/:clientId/open',
            body: openReviewRequestSchema,
            responses: { 200: okBody, 400: errorBody, 404: errorBody },
            summary: 'Open an ad-hoc review ("Start review now")',
        },
        startReview: {
            method: 'POST',
            path: '/client-review/review/:reviewId/start',
            body: z.object({}),
            responses: { 200: okBody, 400: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Begin working a due review (due_soon → in_progress)',
        },
        dispositionStep: {
            method: 'POST',
            path: '/client-review/review/:reviewId/step',
            body: dispositionStepRequestSchema,
            responses: { 200: okBody, 400: errorBody, 404: errorBody },
            summary: 'Disposition one of the four review steps',
        },
        completeReview: {
            method: 'POST',
            path: '/client-review/review/:reviewId/complete',
            body: completeReviewRequestSchema,
            responses: { 200: okBody, 400: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Sign off and complete a review (resets the freshness anchors)',
        },
        cancelReview: {
            method: 'POST',
            path: '/client-review/review/:reviewId/cancel',
            body: cancelReviewRequestSchema,
            responses: { 200: okBody, 400: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Cancel a review opened in error or superseded',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
